import * as React from 'react';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getIsTimedPlusBiddingOpen,
    getIsTimedPlusItemBiddingClosed,
    getItemSummary,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import { longFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { NewAccountWalkthroughSteps } from '@/types/walkthroughs';
import { useAppSelector } from '@/redux/hooks';
import BiddingCountdown from './components/Countdown/BiddingCountdown';
import CatalogDate from '@/components/CatalogDate/CatalogDate';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import isEqual from 'react-fast-compare';
import ms from 'ms';
import styled from 'styled-components';
import TimedPlusCountdown from './components/TimedPlusCountdown/TimedPlusCountdown';
import TimedPlusEndDate from './components/TimedPlusEndDate/TimedPlusEndDate';
import TimedPlusStartDate from './components/TimedPlusStartDate/TimedPlusStartDate';
import TimedPlusTooltip from './components/TimedPlusCountdown/TimedPlusTooltip';
import useIsServerSideRendered from '@/hooks/useIsServerSideRendered';

type Props = {
    catalogId: number;
    itemId: number;
    onCountdownEnd?: () => void;
    showCountdown?: boolean;
};

const ItemBiddingEstimate = ({ catalogId, itemId, onCountdownEnd, showCountdown = true }: Props) => {
    const {
        catalogStatus,
        currency,
        highBidEstimate,
        index,
        isExtended,
        isPassed,
        isSold,
        isTimedPlusAuction,
        lotEndTimeEstimatedTs,
        lowBidEstimate,
        saleStartTs,
    } = useAppSelector((state) => getItemSummary(state, itemId));
    const hasStarted = useAppSelector((state) => getIsTimedPlusBiddingOpen(state, itemId));
    const isClosed = useAppSelector((state) => getIsTimedPlusItemBiddingClosed(state, itemId));
    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));

    // PT-2685 Comment for TIM ---> Was here for when I figured out the countdown to render the countdown only if SSR and the countdown && date if clientside
    const isSsr = useIsServerSideRendered();
    const isCatalogLive = catalogStatus === 'live';
    const itemIndexOffset = index - currentItemIndex;

    const estimateRange = {
        high: (
            <FormattedCurrency
                key="estimate-high"
                noFraction
                sourceCurrency={currency}
                value={highBidEstimate}
            />
        ),
        low: (
            <FormattedCurrency
                key="estimate-low"
                noFraction
                sourceCurrency={currency}
                value={lowBidEstimate}
            />
        ),
    };

    // custom messaging depending on position of lot in a live catalog
    let lotPositionText;
    switch (itemIndexOffset) {
        case 1:
            lotPositionText = <FormattedMessage id="itemBidding.lotAway" />;
            break;
        case -1:
            lotPositionText = <FormattedMessage id="itemBidding.lotAgo" />;
            break;
        case 0:
            lotPositionText = <FormattedMessage id="itemBidding.liveNow" />;
            break;
        default:
            const positionId = itemIndexOffset > 0 ? 'itemBidding.lotsAway' : 'itemBidding.lotsAgo';
            lotPositionText = (
                <FormattedMessage
                    id={positionId}
                    values={{
                        lots: (
                            <FormattedNumber
                                key="lots"
                                value={Math.abs(itemIndexOffset)}
                            />
                        ),
                    }}
                />
            );
            break;
    }

    const currentTimestamp = Date.now();
    const twelveHoursFromNow = currentTimestamp + ms('12h');

    const saleStartTms = saleStartTs * 1000;
    const formattedDate = longFormatDate(saleStartTs);

    const lotEndTimeEstimatedTms = lotEndTimeEstimatedTs * 1000;

    const biddingStartsWithin12Hrs = saleStartTms >= currentTimestamp && saleStartTms <= twelveHoursFromNow;
    const biddingEndsWithin12Hrs =
        lotEndTimeEstimatedTms >= currentTimestamp && lotEndTimeEstimatedTms <= twelveHoursFromNow;

    let timedPlusEnd;
    if (biddingEndsWithin12Hrs) {
        timedPlusEnd = (
            <TimedPlusCountdown
                isExtended={isExtended}
                saleEndTimestamp={lotEndTimeEstimatedTs}
            />
        );
    } else {
        timedPlusEnd = <TimedPlusEndDate saleEndTimestamp={lotEndTimeEstimatedTs} />;
    }

    // show custom lot position message if live catalog
    // show countdown to catalog start if upcoming
    // show countdown to catalog start or extended deadline if timed+
    // or past date if catalog is done
    let countdownText;

    if (biddingStartsWithin12Hrs) {
        countdownText = (
            <StyledBiddingCountdown
                isLive
                onCountdownEnd={onCountdownEnd}
                timestamp={saleStartTs}
            />
        );
    } else {
        countdownText = <BodySecondary>{formattedDate}</BodySecondary>;
    }

    if (isTimedPlusAuction && !isClosed) {
        countdownText = hasStarted ? timedPlusEnd : <TimedPlusStartDate saleStartTimestamp={saleStartTs} />;
    } else if (isCatalogLive && !isTimedPlusAuction) {
        countdownText = (
            <StyledLiveLotPosition
                color="error"
                forwardedAs="div"
                id="lotPosition"
            >
                {lotPositionText}
            </StyledLiveLotPosition>
        );
    } else if (isTimedPlusAuction && isClosed) {
        countdownText = (
            <StyledTimedPlusDoneMessage forwardedAs="div">
                <FormattedMessage
                    id="itemBidding.lotClosedTimedPlus"
                    values={{
                        tooltip: <TimedPlusTooltip key="timed-plus-done-message-tooltip" />,
                    }}
                />
            </StyledTimedPlusDoneMessage>
        );
    } else if (isSold || isPassed || isClosed) {
        countdownText = (
            <StyledCatalogDate
                date={saleStartTs}
                showDate
            />
        );
    }

    return (
        <EstimateRow>
            <StyledEstimateAmounts
                data-walkthrough-step={NewAccountWalkthroughSteps.Item3}
                forwardedAs="div"
            >
                <FormattedMessage
                    id="itemBidding.estimate"
                    values={estimateRange}
                />
            </StyledEstimateAmounts>
            {showCountdown && !isSsr && countdownText}
        </EstimateRow>
    );
};

export default React.memo<Props>(ItemBiddingEstimate, isEqual);

const StyledLiveLotPosition = styled(BodySecondary)`
    margin-left: 4px;
`;

const StyledCatalogDate = styled(CatalogDate)`
    display: block;
`;

const StyledTimedPlusDoneMessage = styled(BodySecondary)`
    display: flex;

    & > div {
        margin-left: 4px;
    }
`;

const EstimateRow = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    @media print {
        text-align: center;

        > span,
        > div {
            margin: 7px auto;
        }
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 4px;
    }
`;

const StyledEstimateAmounts = styled(BodySecondary)`
    display: flex;
    span:first-child {
        margin-left: 5px;
    }
    & > span {
        align-items: initial;
    }
`;

const StyledBiddingCountdown = styled(BiddingCountdown)`
    div:last-child {
        margin: 0;
    }
    @media (max-width: 376px) {
        width: 100%;

        && {
            text-align: left;
        }
    }
`;
